import {
  Button,
  Col,
  ContentItem,
  ContentItems,
  ContentWrapper,
  Drawer,
  Flex,
  LinkText,
  Loading,
  Space,
  Text,
  TitledText,
} from '@/components';
import { PublishedProjectMutableGuard } from '@/features/authentication/_components/PublishedProjectMutableGuard';
import {
  useIndicatorPermission,
  useProjectPermission,
} from '@/features/authentication/_hooks/permission';
import { PinpointMarker } from '@/features/comment/components/pinpoint';
import {
  getPinpointSceneAndFieldBySheetType,
  getPinpointSheetType,
} from '@/features/comment/utils/pinpoint';

import {
  useProject,
  useUpdateProjectCabinetDecisions,
} from '@/features/project/_hooks/project';
import { useId } from '@/hooks';
import { useMessage } from '@/providers';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useProjectDetailDrawerState } from '../../_components/ProjectDrawerStateProvider';
import { CabinetDecisionsForm } from './CabinetDecisionsForm';
import { ConsecutiveIndicatorFormDrawer } from './ConsecutiveIndicatorFormDrawer';
import { ActivitiesAndOutcomesTable } from './activities-and-outcomes/ActivitiesAndOutcomesTable';
import { ActivitiesTable } from './activities/ActivitiesTable';

export const ActivityDetails: FC = () => {
  const { drawerState, setDrawerState } = useProjectDetailDrawerState();
  const { id: projectId } = useId();
  const { t } = useTranslation();
  const { openMessage } = useMessage();

  const project = useProject(projectId);
  const editProjectPermission = useProjectPermission(true);
  const editIndicatorPermission = useIndicatorPermission(true);
  const hasEditPermission =
    editProjectPermission.data && editIndicatorPermission.data;

  const { mutateAsync: updateProjectCabinetDecisionsAsync } =
    useUpdateProjectCabinetDecisions();

  if (!project.data || project.isPending) {
    return <Loading />;
  }

  const pinpointSheetType = getPinpointSheetType(
    project.data.sheetType,
    project.data.fundAdditionalAttributes?.fundSheetType,
  );

  return (
    <Flex vertical gap={8}>
      <ContentWrapper
        title={
          <PinpointMarker
            pinpoint={getPinpointSceneAndFieldBySheetType({
              pinpointSheetType,
              rs: {
                path: { project: projectId },
                field: 'rs.activity-title',
              },
              ss: {
                path: { project: projectId },
                field: 'ss.activity-title',
              },
              ksStandard: {
                path: { project: projectId },
                field: 'ks.standard.activity-title',
              },
              ksLocalGovernment: {
                path: { project: projectId },
                field: 'ks.local-government.activity-title',
              },
            })}
          >
            <Text variant="h3" text={t('activity')} />
          </PinpointMarker>
        }
      >
        <ContentItem borderBottom={0} paddingBottom={0}>
          <ActivitiesTable
            hasEditPermission={!!(hasEditPermission && !project.data?.isLocked)}
          />
        </ContentItem>
      </ContentWrapper>

      <ContentWrapper
        title={
          <PinpointMarker
            pinpoint={getPinpointSceneAndFieldBySheetType({
              pinpointSheetType,
              rs: {
                path: { project: projectId },
                field: 'rs.activities-and-outcomes-title',
              },
              ss: {
                path: { project: projectId },
                field: 'ss.activities-and-outcomes-title',
              },
              ksStandard: {
                path: { project: projectId },
                field: 'ks.standard.activities-and-outcomes-title',
              },
              ksLocalGovernment: {
                path: { project: projectId },
                field: 'ks.local-government.activities-and-outcomes-title',
              },
            })}
          >
            <Text variant="h3" text={t('activities-and-outcomes')} />
          </PinpointMarker>
        }
      >
        <ContentItem borderBottom={0} paddingBottom={0}>
          <ActivitiesAndOutcomesTable
            hasEditPermission={!!(hasEditPermission && !project.data?.isLocked)}
          />
        </ContentItem>
      </ContentWrapper>

      {project.data?.sheetType !== 'SS' && (
        <ContentWrapper>
          <Flex vertical gap={16}>
            <Flex justify="space-between">
              <Text
                variant="h3"
                text={t(
                  'cabinet-decisions-where-related-indicators-are-defined',
                )}
              />
              {hasEditPermission && !project.data?.isLocked && (
                <PublishedProjectMutableGuard>
                  <Col flex="none">
                    <Button
                      type="primary"
                      onClick={() => {
                        setDrawerState({
                          open: true,
                          type: 'cabinet-decisions',
                        });
                      }}
                    >
                      {t('edit')}
                    </Button>
                  </Col>
                </PublishedProjectMutableGuard>
              )}
            </Flex>
            <ContentItem borderBottom={0} paddingBottom={0}>
              <ContentItems
                style={{ marginBottom: 24 }}
                items={[
                  <PinpointMarker
                    key="name"
                    pinpoint={getPinpointSceneAndFieldBySheetType({
                      pinpointSheetType,
                      rs: {
                        path: { project: projectId },
                        field:
                          'rs.cabinet-decision-with-defined-indicators-name',
                      },
                      ksStandard: {
                        path: { project: projectId },
                        field:
                          'ks.standard.cabinet-decision-with-defined-indicators-name',
                      },
                      ksLocalGovernment: {
                        path: { project: projectId },
                        field:
                          'ks.local-government.cabinet-decision-with-defined-indicators-name',
                      },
                    })}
                  >
                    <TitledText
                      title={t('name')}
                      text={
                        project.data.cabinetDecisionWithDefinedIndicators?.name
                      }
                    />
                  </PinpointMarker>,
                  <PinpointMarker
                    key="name"
                    pinpoint={getPinpointSceneAndFieldBySheetType({
                      pinpointSheetType,
                      rs: {
                        path: { project: projectId },
                        field:
                          'rs.cabinet-decision-with-defined-indicators-url',
                      },
                      ksStandard: {
                        path: { project: projectId },
                        field:
                          'ks.standard.cabinet-decision-with-defined-indicators-url',
                      },
                      ksLocalGovernment: {
                        path: { project: projectId },
                        field:
                          'ks.local-government.cabinet-decision-with-defined-indicators-url',
                      },
                    })}
                  >
                    <Space key="url" direction="vertical">
                      <Text color="secondary" text={t('url')} />
                      <LinkText
                        text={
                          project.data.cabinetDecisionWithDefinedIndicators
                            ?.url ?? undefined
                        }
                        href={
                          project.data.cabinetDecisionWithDefinedIndicators
                            ?.url ?? ''
                        }
                      />
                    </Space>
                  </PinpointMarker>,
                ]}
              />
              <ContentItems
                style={{ marginBottom: 24 }}
                items={[
                  <PinpointMarker
                    key="name"
                    pinpoint={getPinpointSceneAndFieldBySheetType({
                      pinpointSheetType,
                      rs: {
                        path: { project: projectId },
                        field:
                          'rs.cabinet-decision-with-defined-indicators-relevant-part',
                      },
                      ksStandard: {
                        path: { project: projectId },
                        field:
                          'ks.standard.cabinet-decision-with-defined-indicators-relevant-part',
                      },
                      ksLocalGovernment: {
                        path: { project: projectId },
                        field:
                          'ks.local-government.cabinet-decision-with-defined-indicators-relevant-part',
                      },
                    })}
                  >
                    <TitledText
                      key="relevant-part"
                      title={t('relevant-part')}
                      text={
                        project.data.cabinetDecisionWithDefinedIndicators
                          ?.relevantPart
                      }
                    />
                  </PinpointMarker>,
                ]}
              />
            </ContentItem>
          </Flex>
        </ContentWrapper>
      )}

      {hasEditPermission && (
        <>
          <Drawer
            open={drawerState.open && drawerState.type === 'cabinet-decisions'}
            onClose={() => {
              setDrawerState({ open: false });
            }}
            destroyOnClose
          >
            <CabinetDecisionsForm
              initialValues={
                project.data.cabinetDecisionWithDefinedIndicators ?? undefined
              }
              onSubmit={async (values) => {
                await updateProjectCabinetDecisionsAsync({
                  id: projectId,
                  ...values,
                });
                await Promise.all([
                  openMessage('success', t('message-update-completed')),
                  (async () => {
                    setDrawerState({ open: false });
                  })(),
                ]);
              }}
            />
          </Drawer>

          <ConsecutiveIndicatorFormDrawer
            fiscalYear={project.data.fiscalYear}
          />
        </>
      )}
    </Flex>
  );
};
